import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Question, MagnifyingGlass, ArrowRight, WhatsappLogo, TrendUp } from "@phosphor-icons/react";
import { LeadForm } from "./LeadForm";
import { getFilterOptions } from "../lib/api";
import { STATE_TO_BODY } from "../lib/categoryMap";

// Common India states shown in the Domicile dropdown.
const STATES = [
  "All India", "Andhra Pradesh", "Assam", "Bihar", "Chhattisgarh", "Delhi",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu & Kashmir",
  "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra",
  "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Puducherry",
  "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
];

// ────────────────────────────────────────────────────────────────────────
// computeVerdict — SIMPLIFIED
//
// The dropdown now hands us an EXACT `{body, category}` pair (e.g. MCC:OP
// or Karnataka:1G). We look up the matching MBBS cutoff rows for the
// latest year and turn the worst closing rank into a verdict.
// ────────────────────────────────────────────────────────────────────────
function computeVerdict({ cutoffs, rank, body, category }) {
  if (!rank || !body || !category || !cutoffs || cutoffs.length === 0) {
    return { verdict: "no-data", bestClose: null, matchedRow: null };
  }
  const userRank = parseInt(rank, 10);
  if (!Number.isFinite(userRank) || userRank <= 0) {
    return { verdict: "no-data", bestClose: null, matchedRow: null };
  }
  const latestYear = Math.max(...cutoffs.map((c) => c.year || 0));
  const rows = cutoffs.filter((c) =>
    (c.course || "MBBS").toUpperCase() === "MBBS"
    && c.year === latestYear
    && c.counselling_body === body
    && (c.category || "").toUpperCase() === category.toUpperCase(),
  );
  if (rows.length === 0) return { verdict: "no-data", bestClose: null, matchedRow: null };

  const bestClose = Math.max(...rows.map((r) => r.allotted_rank || 0));
  const matchedRow = rows.find((r) => r.allotted_rank === bestClose) || rows[0];
  let verdict;
  if (userRank <= bestClose) verdict = "safe";
  else if (userRank <= bestClose * 1.15) verdict = "moderate";
  else verdict = "not-eligible";
  return { verdict, bestClose, matchedRow };
}

/**
 * Compact predictor panel embedded in the CollegeDetail right sidebar. Given
 * a rank + category + domicile the student can instantly see if they qualify
 * for THIS college (green tick) or need to explore others (button to jump to
 * the full predictor pre-populated with the same inputs).
 *
 * Categories are loaded live from `/api/filter-options` and filtered to just
 * MCC + the college's home state body — no manual mapping tables. Domicile
 * remains as a soft context signal + is forwarded to the full predictor.
 */
export const EligibilityPredictor = ({ college, cutoffs = [] }) => {
  const [rank, setRank] = useState("");
  const [domicile, setDomicile] = useState("All India");
  // Selected option is a "<body>|<category>" composite key.
  const [selected, setSelected] = useState("MCC|OP");
  const [checked, setChecked] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [catsGrouped, setCatsGrouped] = useState(null);
  const navigate = useNavigate();

  // Fetch categories_grouped once and cache in module state.
  useEffect(() => {
    getFilterOptions()
      .then((r) => setCatsGrouped(r.data?.categories_grouped || {}))
      .catch(() => setCatsGrouped({}));
  }, []);

  // Category options for THIS college — MCC always, plus the college's
  // home-state body (Karnataka → 76 codes; Bihar → 8 codes; MCC → 13 codes).
  const collegeBody = STATE_TO_BODY[college?.state] || null;
  const options = useMemo(() => {
    if (!catsGrouped) return [];
    const list = [];
    (catsGrouped.MCC || []).forEach((c) =>
      list.push({ value: `MCC|${c}`, body: "MCC", category: c, label: `MCC · ${c}` }),
    );
    if (collegeBody && catsGrouped[collegeBody]) {
      catsGrouped[collegeBody].forEach((c) =>
        list.push({ value: `${collegeBody}|${c}`, body: collegeBody, category: c, label: `${collegeBody.replace(/_/g, " ")} · ${c}` }),
      );
    }
    return list;
  }, [catsGrouped, collegeBody]);

  const selectedOpt = useMemo(
    () => options.find((o) => o.value === selected) || options[0],
    [options, selected],
  );

  // If the list changes and the current selection isn't in it, reset.
  useEffect(() => {
    if (options.length > 0 && !options.some((o) => o.value === selected)) {
      setSelected(options[0].value);
      setChecked(false);
    }
  }, [options, selected]);

  const verdict = useMemo(
    () => computeVerdict({
      cutoffs, rank,
      body: selectedOpt?.body,
      category: selectedOpt?.category,
    }),
    [cutoffs, rank, selectedOpt],
  );

  const canCheck = rank && selectedOpt;
  const showVerdict = checked && canCheck;

  const goToPredictor = () => {
    const params = new URLSearchParams();
    if (rank) params.set("rank", rank);
    // Send the granular category directly — the full predictor accepts arbitrary
    // body-specific codes and will pre-select them.
    if (selectedOpt?.category) params.set("category", selectedOpt.category);
    if (domicile && domicile !== "All India") params.set("domicile", domicile);
    navigate(`/ug-neet/predict?${params.toString()}`);
  };

  return (
    <div className="border-2 border-[#6043EF]/25 bg-white rounded-2xl overflow-hidden shadow-[0_4px_20px_-8px_rgba(96,67,239,0.25)]" data-testid="eligibility-predictor">
      <div className="px-4 py-3 bg-gradient-to-r from-[#6043EF] to-violet-500 text-white flex items-center gap-2">
        <MagnifyingGlass size={16} weight="fill" />
        <span className="text-xs font-bold uppercase tracking-[0.2em]">Am I Eligible?</span>
      </div>

      <div className="p-4 space-y-3">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">NEET Rank *</label>
          <input
            type="number" min="1" value={rank}
            onChange={(e) => { setRank(e.target.value); setChecked(false); }}
            placeholder="e.g. 15000"
            data-testid="predictor-rank-input"
            className="w-full h-10 px-3 border-2 border-zinc-200 rounded-lg text-sm font-mono font-bold text-[#09090B] focus:border-[#6043EF] focus:outline-none"
          />
        </div>

        {/* Compact 2-col: Category (granular) + Domicile side-by-side */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">
              Category
              {collegeBody && (
                <span className="ml-1 font-normal normal-case tracking-normal text-zinc-400 text-[9px]">· MCC + {collegeBody.replace(/_/g, " ")}</span>
              )}
            </label>
            <select
              value={selected}
              onChange={(e) => { setSelected(e.target.value); setChecked(false); }}
              data-testid="predictor-category-select"
              className="w-full h-10 px-2 border-2 border-zinc-200 rounded-lg text-xs font-bold text-[#09090B] focus:border-[#6043EF] focus:outline-none bg-white"
            >
              {options.length === 0 && <option value="">Loading…</option>}
              {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">Domicile</label>
            <select
              value={domicile}
              onChange={(e) => { setDomicile(e.target.value); setChecked(false); }}
              data-testid="predictor-domicile-select"
              className="w-full h-10 px-2 border-2 border-zinc-200 rounded-lg text-xs font-bold text-[#09090B] focus:border-[#6043EF] focus:outline-none bg-white"
            >
              {STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        <button
          type="button" onClick={() => setChecked(true)} disabled={!canCheck}
          data-testid="predictor-check-btn"
          className="w-full h-10 rounded-lg bg-[#09090B] hover:bg-[#6043EF] text-white text-xs font-bold uppercase tracking-[0.15em] flex items-center justify-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <MagnifyingGlass size={13} weight="bold" /> Check Eligibility
        </button>

        {/* Verdict card */}
        {showVerdict && (
          <VerdictCard verdict={verdict} rank={rank} />
        )}

        {/* Two CTAs after check */}
        {showVerdict && (
          <div className="space-y-2 pt-1">
            {(verdict.verdict === "safe" || verdict.verdict === "moderate") && (
              <button
                type="button" onClick={() => setShowLead(true)}
                data-testid="predictor-apply-btn"
                className="w-full h-10 rounded-lg bg-gradient-to-r from-[#00C04B] to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-xs font-bold uppercase tracking-[0.15em] flex items-center justify-center gap-1.5 transition-all shadow-[0_2px_8px_-2px_rgba(0,192,75,0.5)]"
              >
                Apply to This College <ArrowRight size={13} weight="bold" />
              </button>
            )}
            <button
              type="button" onClick={goToPredictor}
              data-testid="predictor-more-btn"
              className="w-full h-10 rounded-lg border-2 border-[#6043EF] bg-white hover:bg-[#6043EF]/5 text-[#6043EF] text-xs font-bold uppercase tracking-[0.15em] flex items-center justify-center gap-1.5 transition-colors"
            >
              <TrendUp size={13} weight="bold" /> More Eligible Colleges
            </button>
          </div>
        )}
      </div>

      <LeadForm open={showLead} onOpenChange={setShowLead} college={college} />
    </div>
  );
};

const VerdictCard = ({ verdict, rank }) => {
  const { verdict: v, bestClose, matchedRow } = verdict;
  const rankNum = parseInt(rank, 10);

  if (v === "no-data") {
    return (
      <div className="p-3 border-2 border-dashed border-zinc-200 rounded-lg text-center" data-testid="predictor-verdict-no-data">
        <Question size={22} weight="bold" className="text-zinc-400 mx-auto mb-1" />
        <p className="text-xs text-zinc-500 font-semibold">No cutoff data yet</p>
        <p className="text-[10px] text-zinc-400 mt-0.5">This is likely a first-year college. Enquire directly below.</p>
      </div>
    );
  }

  const conf = {
    safe: {
      Icon: CheckCircle,
      label: "Eligible",
      sub: "Your rank is within the closing rank",
      bg: "from-emerald-50 to-emerald-100",
      border: "border-emerald-300",
      accent: "text-emerald-700",
      iconColor: "text-emerald-600",
    },
    moderate: {
      Icon: Question,
      label: "Borderline",
      sub: "Within 15% margin — worth applying",
      bg: "from-amber-50 to-amber-100",
      border: "border-amber-300",
      accent: "text-amber-700",
      iconColor: "text-amber-600",
    },
    "not-eligible": {
      Icon: XCircle,
      label: "Above closing rank",
      sub: "See other colleges that fit your rank",
      bg: "from-rose-50 to-rose-100",
      border: "border-rose-300",
      accent: "text-rose-700",
      iconColor: "text-rose-600",
    },
  }[v];

  return (
    <div className={`p-3 border-2 ${conf.border} bg-gradient-to-br ${conf.bg} rounded-lg`} data-testid={`predictor-verdict-${v}`}>
      <div className="flex items-start gap-2">
        <conf.Icon size={24} weight="fill" className={`${conf.iconColor} shrink-0`} />
        <div className="min-w-0">
          <div className={`text-sm font-bold ${conf.accent}`}>{conf.label}</div>
          <div className="text-[11px] text-zinc-600 mt-0.5">{conf.sub}</div>
          {bestClose != null && (
            <div className="mt-1.5 text-[11px] text-zinc-500 space-x-2 font-mono">
              <span>Your rank <b className="text-[#09090B]">{rankNum.toLocaleString("en-IN")}</b></span>
              <span>·</span>
              <span>Closing <b className="text-[#09090B]">{bestClose.toLocaleString("en-IN")}</b></span>
              {matchedRow?.counselling_body && (
                <span className="inline-block px-1 py-0.5 bg-white/80 text-[9px] font-bold uppercase text-zinc-600 rounded ml-1">
                  {matchedRow.counselling_body === "MCC" ? "MCC" : (matchedRow.counselling_body || "").replace(/_/g, " ")} · {matchedRow.category} · {matchedRow.year}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Get Priority Callback — sidebar card. Opens the full LeadForm modal on click.
 * Compact version to sit alongside the EligibilityPredictor and Campus gallery.
 */
export const LeadCallbackCard = ({ college }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-4 shadow-[0_4px_20px_-8px_rgba(234,88,12,0.25)]" data-testid="lead-callback-card">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center text-white shrink-0">
            <WhatsappLogo size={18} weight="fill" />
          </div>
          <div className="text-sm font-bold text-[#09090B] leading-tight">Get Priority Callback</div>
        </div>

        {/* Bold, big value-prop line filling the card body */}
        <p className="text-xl sm:text-2xl font-extrabold text-[#09090B] leading-tight mb-3 font-heading">
          Fees, seats &amp; cutoffs
          <span className="block bg-gradient-to-r from-orange-600 to-amber-700 bg-clip-text text-transparent">in 24 hours.</span>
        </p>

        <button
          type="button" onClick={() => setOpen(true)}
          data-testid="lead-callback-btn"
          className="w-full h-10 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white text-xs font-bold uppercase tracking-[0.15em] flex items-center justify-center gap-1.5 transition-all shadow-[0_2px_8px_-2px_rgba(234,88,12,0.6)]"
        >
          Request Callback <ArrowRight size={13} weight="bold" />
        </button>
      </div>
      <LeadForm open={open} onOpenChange={setOpen} college={college} />
    </>
  );
};

export default EligibilityPredictor;
